import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { Divider, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { ILeadSearchFilter, getAllLeadStatusByVisibility } from 'wisestep-api';
import {
  formatDateToMonthDayYearString,
  SearchBarNew,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsButton,
  WsColors,
  WsColorsNew,
  WsSelect,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { StyledBox } from '../../../features/clientListingPage/helper/ClientListingHelper';
import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { allowedKeys, defaultLeadSearchFilter } from '../helper/leadHelper';

import { StatusList } from './LeadDetailsForm';

export interface ICreatedOrUpdatedBetween {
  filterByDateType: string;
  fromDate: string;
  toDate: string;
}

export interface IClientContactFilter {
  id: string;
  firstName: string;
  emailAddress: string;
}

export interface IClientFilter {
  id: string;
  name: string;
}

export interface ILeadsFilters {
  onAdvancedFiltersClick: () => void;
  searchQuery: ILeadSearchFilter;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  anyLeadsSelected: boolean;
  onRandomAssign: () => void;
  onBulkAssign: () => void;
  selectedLeadsCount?: number;
  onAddLead: () => void;
  advFilterApply: ILeadSearchFilter;
}

const LeadFilters = ({
  onAdvancedFiltersClick,
  searchQuery,
  getUpdatedFiltersFromFilters,
  anyLeadsSelected,
  onRandomAssign,
  onBulkAssign,
  selectedLeadsCount,
  advFilterApply,
  clear,
}: ILeadsFilters) => {
  const methods = useForm({
    defaultValues: searchQuery ?? defaultLeadSearchFilter,
  });
  const { getValues, setValue, reset } = methods;

  const [statusList, setStatusList] = useState<StatusList[]>([]);
  const [applyedStatus, setApplyedStatus] = useState<string[]>(searchQuery?.status ?? []);

  useEffect(() => {
    setApplyedStatus(searchQuery?.status ?? []);
  }, [searchQuery]);

  const onSearch = () => {
    getUpdatedFiltersFromFilters({
      ...searchQuery,
      ...getValues(),
    });
  };

  const onClear = () => {
    setValue('status', []);
    getUpdatedFiltersFromFilters(defaultLeadSearchFilter);
    reset({ ...defaultLeadSearchFilter, status: [] });
    clear();
    onSearch();
  };

  useEffect(() => {
    const getStatusList = async () => {
      const response = await getAllLeadStatusByVisibility();
      const statusData: StatusList[] = [];
      response.data?.forEach((statusObj) => {
        statusData.push({
          label: statusObj.leadStatusName as string,
          value: statusObj.id as string,
        });
      });
      setStatusList(statusData);
    };
    getStatusList();
  }, []);

  const hasValue = (val: ILeadSearchFilter): boolean => {
    if (Array.isArray(val)) return val.length > 0;
    if (val && typeof val === 'object') return Object.values(val).some(hasValue);
    return Boolean(val);
  };

  const hasFilterValue = (): boolean => {
    const hasContactStatus = (applyedStatus?.length ?? 0) > 0;
    type AllowedKey = keyof typeof advFilterApply;

    const filtered = allowedKeys.reduce((acc, key) => {
      acc[key] = advFilterApply[key];
      return acc;
    }, {} as Pick<typeof advFilterApply, AllowedKey>);
    const advFilterCheck = hasValue(filtered);
    return hasContactStatus || advFilterCheck;
  };

  const onCreatedOrUpdatedBetweenClear = () => {
    setValue('filterByDateType', '');
    setValue('fromDate', '');
    setValue('toDate', '');
    onSearch();
  };

  const onClearClientContacts = (value: string) => {
    const fieldValues = getValues('contactIds');

    const updatedArr =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item.id !== value)
        : undefined;

    setValue('contactIds', updatedArr);
    onSearch();
  };

  const onClearClient = (value: string) => {
    const fieldValues = getValues('clientIds');

    const updatedArr =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item.id !== value)
        : undefined;

    setValue('clientIds', updatedArr);
    onSearch();
  };

  const onClearLeadOwners = (value: string) => onFilterClear('leadOwners', value);

  const onClearTags = (value: string) => onFilterClear('tags', value);

  const onClearSource = (value: string) => onFilterClear('source', value);

  const onCreatedByIdClear = (value: string) => onFilterClear('createdByIds', value);

  const onFilterClear = (
    field: 'source' | 'tags' | 'leadOwners' | 'createdByIds',
    value: string
  ) => {
    const values = getValues();
    const fieldValues = values[field];
    const updatedValues: string[] | undefined =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item !== value)
        : undefined;
    setValue(field, updatedValues);
    onSearch();
  };

  return (
    <StyledBox sx={{ border: 'none' }}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSearch)}>
          <Grid container bgcolor={WsColors.White} sx={{ ml: -1 }}>
            <Grid item xs={4}>
              <FormProvider {...methods}>
                <SearchBarNew size="small" onSearch={onSearch} name="query" />
              </FormProvider>
            </Grid>
            <Grid item xs={4} spacing={1} sx={{ mt: 0.2 }}>
              <SmartFilter
                filterName="Status"
                onClick={onSearch}
                filterValue={getFilterValue('status', methods)}
                onClear={onClear}
              >
                <WsSelect
                  name="status"
                  selectArray={statusList}
                  placeholder="Select Lead Status"
                  required
                />
              </SmartFilter>
            </Grid>
            <Grid item xs={4}>
              <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
                {anyLeadsSelected ? (
                  <>
                    <Grid item>
                      <WsTypography color={WsTypoColors.PRIMARY600} bold>
                        {selectedLeadsCount} selected
                      </WsTypography>
                    </Grid>
                    <Grid item>
                      <WsButton
                        variant="contained"
                        size="medium"
                        disabled={selectedLeadsCount === 0}
                        onClick={onBulkAssign}
                        startIcon={<PersonAddAlt1OutlinedIcon />}
                      >
                        Assign Lead Owner
                      </WsButton>
                    </Grid>
                    <Grid item>
                      <WsButton
                        variant="contained"
                        size="medium"
                        startIcon={<LibraryAddCheckOutlinedIcon />}
                        onClick={onRandomAssign}
                      >
                        Assign Randomly
                      </WsButton>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item>
                      <WsButton
                        variant="outlined"
                        size="medium"
                        onClick={onAdvancedFiltersClick}
                        startIcon={<BiFilterAlt />}
                      >
                        Advanced filters
                      </WsButton>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid container pt={2} spacing={1} style={{ display: 'flex', alignItems: 'center' }}>
            {typeof applyedStatus === 'string' && (
              <Grid item>
                <WsBadge
                  label={`Status: ${
                    statusList.find((item) => item.value === applyedStatus)?.label
                  }`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={onClear}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            )}

            {advFilterApply?.createdByIds?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Created By: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onCreatedByIdClear?.(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {advFilterApply?.source?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Source: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearSource?.(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}
            {advFilterApply?.tags?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Tags: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearTags?.(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}
            {advFilterApply?.filterByDateType &&
              advFilterApply?.fromDate &&
              advFilterApply?.toDate && (
                <Grid item>
                  <WsBadge
                    label={`${
                      advFilterApply?.filterByDateType === 'CREATED_BETWEEN' ? 'Created' : 'Updated'
                    } Between: ${formatDateToMonthDayYearString(
                      new Date(advFilterApply?.fromDate ?? '')
                    )} - ${formatDateToMonthDayYearString(new Date(advFilterApply?.toDate ?? ''))}`}
                    badgeType={WsBadgetype.INACTIVE}
                    endIcon={true}
                    onClick={() => onCreatedOrUpdatedBetweenClear?.()}
                    badgeSize={WsBadgeSize.LARGE}
                  />
                </Grid>
              )}

            {advFilterApply?.leadOwners?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Lead Owner: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearLeadOwners?.(label)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {advFilterApply?.clientIds?.map((item, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Client: ${item.name}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearClient?.(item.id)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {advFilterApply?.contactIds?.map((item, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Contact: ${item.firstName}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => onClearClientContacts?.(item.id)}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}
            {hasFilterValue() && (
              <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    backgroundColor: WsColorsNew.neutral_400a,
                    height: 30,
                    mx: 0.5,
                  }}
                />
                <Grid item pl={0.5}>
                  <WsButton onClick={onClear} variant="text">
                    Clear Filters
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </StyledBox>
  );
};
export default LeadFilters;
